import React, { FC, useEffect, useState } from "react"
import { StreamsChartData, StreamTrackMonth } from "../../../../types"
import { KSpan, KTooltip } from "kahuna-base-react-components"
import NotFound from "../../../NotFound"
import { lang } from "../../../../constants/languages"
import { formatNumber } from "../../../../utility"
import InfiniteScroll from "react-infinite-scroll-component"
import { Chart as ChartJS, LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend, Filler } from "chart.js"
import { Line } from "react-chartjs-2"

ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend, Filler)

type StreamsTrackMonthsProps = {
  tracks: StreamTrackMonth[]
  chartData: StreamsChartData[]
  totalStreams: number
  missingDays: string[]
}

const StreamsTrackMonths: FC<StreamsTrackMonthsProps> = (props) => {
  const { tracks, chartData, totalStreams, missingDays } = props

  const [hoverId, setHoverId] = useState<string>()
  const [items, setItems] = useState<StreamTrackMonth[]>([])
  const [hasMore, setHasMore] = useState(true)
  const ITEMS_PER_LOAD = 200

  useEffect(() => {
    setItems(tracks.slice(0, ITEMS_PER_LOAD))
    setHasMore(true)
  }, [tracks])

  const fetchMoreData = () => {
    const nextItems = tracks.slice(items.length, items.length + ITEMS_PER_LOAD)
    setItems((prev) => [...prev, ...nextItems])
    if (items.length + nextItems.length >= tracks.length) {
      setHasMore(false)
    }
  }

  const lineChartData = {
    labels: chartData.map((item) => item.monthLabel),
    datasets: [
      {
        label: "Streams",
        data: chartData.map((item) => item.streams),
        borderColor: "#56E099",
        backgroundColor: "rgba(86, 224, 153, 0.1)",
        fill: true,
        tension: 0.4,
        pointRadius: 3,
        pointHoverRadius: 6,
        pointBackgroundColor: "#56E099"
      }
    ]
  }

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        mode: "index" as const,
        intersect: false,
        callbacks: {
          label: (context: any) => `Streams: ${formatNumber(context.raw)}`
        }
      },
      datalabels: { display: false }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: "#666" }
      },
      y: {
        grid: { color: "#f0f0f0" },
        ticks: {
          color: "#666",
          callback: (value: any) => formatNumber(value)
        },
        beginAtZero: true
      }
    }
  }

  return (
    <div className="w-full h-full flex flex-col gap-4">
      {/* Chart Section */}
      <div
        className="py-5 px-7"
        style={{
          borderRadius: "10px",
          border: "1px solid var(--stroke-soft-200, #F3F3F3)",
          background: "var(--bg-white-0, #FFF)",
          boxShadow: "0px 2px 4px 0px rgba(27, 28, 29, 0.04)"
        }}
      >
        <div className="flex items-center gap-4 mb-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#56E099" }}></div>
            <KSpan text="Streams" fontWeight={500} />
          </div>
          <KSpan text={formatNumber(totalStreams)} fontWeight={600} fontSize={18} />
        </div>
        <div style={{ height: 200 }}>
          {chartData.length > 0 ? (
            <Line data={lineChartData} options={lineChartOptions} />
          ) : (
            <div className="flex items-center justify-center h-full">
              <KSpan text="No chart data available" color="#999" />
            </div>
          )}
        </div>
      </div>

      {/* Table Section */}
      <div
        className="py-5 px-7 min-h-[100%]"
        style={{
          borderRadius: "10px",
          border: "1px solid var(--stroke-soft-200, #F3F3F3)",
          background: "var(--bg-white-0, #FFF)",
          boxShadow: "0px 2px 4px 0px rgba(27, 28, 29, 0.04)"
        }}
      >
        <div>
          <div className="flex mb-4" style={{ width: "100%" }}>
            <div style={{ width: "26%" }}>
              <KSpan color="#111" text={"Track Title"} />
            </div>
            <div style={{ width: "26%" }}>
              <KSpan color="#111" text={"Artist(s)"} />
            </div>
            <div style={{ width: "13%" }}>
              <KSpan color="#111" text={"60-90 Days Ago"} />
            </div>
            <div style={{ width: "13%" }}>
              <KSpan color="#111" text={"30-60 Days Ago"} />
            </div>
            <div style={{ width: "13%" }} className="flex items-center gap-1">
              <KSpan color="#111" text={"Last 30 Days"} />
              {missingDays && missingDays.length > 0 && (
                <KTooltip
                  backgroundColor="#000"
                  padding="8px 12px"
                  arrowColor="#000"
                  width="160px"
                  position="top"
                  content={
                    <div className="flex flex-col gap-1">
                      <KSpan text="Missing data for:" color="#FFF" fontSize={12} fontWeight={600} />
                      {missingDays.slice(0, 10).map((day) => (
                        <KSpan key={day} text={day} color="#FFF" fontSize={11} />
                      ))}
                      {missingDays.length > 10 && (
                        <KSpan text={`...and ${missingDays.length - 10} more days`} color="#999" fontSize={11} />
                      )}
                    </div>
                  }
                >
                  <div className="cursor-help flex items-center">
                    <img src="/account_icons/info-circle.svg" alt="info" style={{ width: "16px", height: "16px" }} />
                  </div>
                </KTooltip>
              )}
            </div>
            <div style={{ width: "9%" }}>
              <KSpan color="#111" text={"Total"} />
            </div>
          </div>
          {tracks && !(tracks.length > 0) && (
            <div className="flex p-5 w-full justify-center mt-[100px]">
              <NotFound title={lang.not_found.trends.title} description={lang.not_found.trends.desc} imageWidth={400} />
            </div>
          )}
          <div className="flex p-2" style={{ width: "100%", background: "#00000008", borderRadius: "5px" }}>
            <div style={{ width: "52%" }}>
              <KSpan fontWeight={600} color="#111" text={"TOTAL"} />
            </div>
            <div style={{ width: "13%" }}>
              <KSpan
                fontWeight={600}
                color="#111"
                text={formatNumber(tracks?.reduce((sum, item) => sum + (item.streamsThreeMonths || 0), 0) || 0)}
              />
            </div>
            <div style={{ width: "13%" }}>
              <KSpan
                fontWeight={600}
                color="#111"
                text={formatNumber(tracks?.reduce((sum, item) => sum + (item.streamsLastMonth || 0), 0) || 0)}
              />
            </div>
            <div style={{ width: "13%" }}>
              <KSpan
                fontWeight={600}
                color="#111"
                text={formatNumber(tracks?.reduce((sum, item) => sum + (item.streamsThisMonth || 0), 0) || 0)}
              />
            </div>
            <div style={{ width: "9%" }}>
              <KSpan
                fontWeight={600}
                color="#111"
                text={formatNumber(tracks?.reduce((sum, item) => sum + (item.total || 0), 0) || 0)}
              />
            </div>
          </div>
          <InfiniteScroll
            dataLength={items.length}
            next={fetchMoreData}
            hasMore={hasMore}
            loader={<h4>Loading...</h4>}
            height={600}
            endMessage={
              <div className="mt-4" style={{ textAlign: "center" }}>
                <KSpan text="No more tracks" />
              </div>
            }
          >
            {items.map((StreamTrackMonth) => {
              const background = hoverId === StreamTrackMonth.isrc ? "#00000008" : "transparent"
              return (
                <div
                  className="flex p-2"
                  style={{ width: "100%", background, borderRadius: "5px" }}
                  key={`trend_top_track_${StreamTrackMonth.isrc}}`}
                  onMouseOver={() => setHoverId(StreamTrackMonth.isrc)}
                >
                  <div style={{ width: "26%" }}>
                    <KSpan text={StreamTrackMonth.track?.title || "-"} />
                  </div>
                  <div style={{ width: "26%" }}>
                    <KSpan text={StreamTrackMonth.track?.artistList?.map((artist) => artist.name)?.join(", ") || "-"} />
                  </div>
                  <div style={{ width: "13%" }}>
                    <KSpan text={formatNumber(StreamTrackMonth.streamsThreeMonths || 0)} />
                  </div>
                  <div style={{ width: "13%" }}>
                    <KSpan text={formatNumber(StreamTrackMonth.streamsLastMonth || 0)} />
                  </div>
                  <div style={{ width: "13%" }}>
                    <KSpan text={formatNumber(StreamTrackMonth.streamsThisMonth || 0)} />
                  </div>
                  <div style={{ width: "9%" }}>
                    <KSpan text={formatNumber(StreamTrackMonth.total || 0)} />
                  </div>
                </div>
              )
            })}
          </InfiniteScroll>
        </div>
      </div>
    </div>
  )
}

export default StreamsTrackMonths
